import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import TopNavigation from '../components/TopNavigation';
import { authService, ResetPasswordRequest, ValidateResetTokenResponse } from '@/services/authService';

interface ResetPasswordPageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToForgotPassword?: () => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToForgotPassword,
  onNavigateToSignup,
  onToggleMenu
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [tokenValid, setTokenValid] = useState<boolean>(false);

  // Form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Password validation state
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const themeClasses = isDarkMode ? 'text-white' : 'bg-white text-gray-900';

  // Extract token from URL and validate it
  useEffect(() => {
    const validateToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenParam = urlParams.get('token');

      console.log('Reset password page loaded');
      console.log('Token from URL:', tokenParam);

      if (!tokenParam) {
        setError('No reset token provided. Please check your email for the correct reset link.');
        setIsValidatingToken(false);
        return;
      }

      setToken(tokenParam);

      try {
        const validation: ValidateResetTokenResponse = await authService.validateResetToken(tokenParam);

        if (validation.valid) {
          setTokenValid(true);
          setUserEmail(validation.email);
          console.log('Token is valid for email:', validation.email);
        } else {
          setError('This reset link is invalid or has expired. Please request a new password reset.');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to validate reset token';

        if (errorMessage.includes('expired')) {
          setError('This reset link has expired. Please request a new password reset.');
        } else if (errorMessage.includes('invalid')) {
          setError('This reset link is invalid. Please request a new password reset.');
        } else {
          setError('Unable to validate reset link. Please try again or request a new password reset.');
        }
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, []);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Real-time password validation
    if (name === 'password') {
      setPasswordErrors(validatePassword(value));
    }

    // Clear errors when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate form
      if (!formData.password) {
        throw new Error('Please enter a new password');
      }
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Validate password requirements
      const passwordValidationErrors = validatePassword(formData.password);
      if (passwordValidationErrors.length > 0) {
        throw new Error(passwordValidationErrors[0]);
      }

      const resetRequest: ResetPasswordRequest = {
        token: token,
        new_password: formData.password
      };

      const response = await authService.resetPassword(resetRequest);

      setSuccess(response.message);

      // Clear form
      setFormData({
        password: '',
        confirmPassword: ''
      });
      setPasswordErrors([]);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        if (onNavigateToLogin) {
          onNavigateToLogin();
        }
      }, 3000);

    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';

      if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        setError('This reset link has expired or is invalid. Please request a new password reset.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? '#1C1C1E' : 'white' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
      />

      {/* Main Content */}
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 py-8 pb-24 sm:pb-20">
        <div className="w-full max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-regular mb-2">Set New Password</h1>
            {userEmail && (
              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Setting new password for <span className="font-medium">{userEmail}</span>
              </p>
            )}
          </div>

          {/* Reset Form */}
          <div className="max-w-md mx-auto">
            <div className={`p-8 rounded-lg border ${
              isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-gray-50 border-gray-200'
            }`}>

              {/* Loading State - Token Validation */}
              {isValidatingToken && (
                <div className="mb-6 flex items-center justify-center p-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Validating reset link...
                  </span>
                </div>
              )}

              {/* Error Message */}
              {error && !isValidatingToken && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{error}</span>
                  </div>
                  {(error.includes('expired') || error.includes('invalid')) && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm">
                      <p className="font-medium mb-1">Need a new reset link?</p>
                      <button
                        onClick={onNavigateToForgotPassword}
                        className="text-red-600 hover:text-red-700 underline"
                      >
                        Request a new password reset
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-xl text-center">
                  <div className="flex flex-col items-center space-y-4">
                    {/* Success Icon */}
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>

                    {/* Success Text */}
                    <div>
                      <h3 className="text-xl font-semibold text-green-800 mb-2">Password Reset Successfully!</h3>
                      <p className="text-green-700 mb-4">
                        {success}
                      </p>
                      <p className="text-sm text-green-600">
                        Redirecting you to the sign in page in 3 seconds...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reset Form - Show only if token is valid and no success */}
              {tokenValid && !success && !isValidatingToken && (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="password" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      New Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 rounded-full border ${
                        isDarkMode
                          ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                          : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter your new password"
                      disabled={isLoading}
                    />
                    {/* Password Requirements */}
                    {formData.password && passwordErrors.length > 0 && (
                      <div className="mt-2 text-sm text-red-600">
                        <ul className="list-disc list-inside space-y-1">
                          {passwordErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {formData.password && passwordErrors.length === 0 && (
                      <div className="mt-2 text-sm text-green-600">
                        ✓ Password meets all requirements
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 rounded-full border ${
                        isDarkMode
                          ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                          : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Confirm your new password"
                      disabled={isLoading}
                    />
                    {/* Password Match Indicator */}
                    {formData.confirmPassword && (
                      <div className={`mt-2 text-sm ${
                        formData.password === formData.confirmPassword ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formData.password === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white transition-colors ${
                      isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    }`}
                  >
                    {isLoading ? 'Resetting Password...' : 'Reset Password'}
                  </button>
                </form>
              )}

              {/* Back to Login - Show always except during success */}
              {!success && (
                <div className="mt-6 text-center">
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Remember your password?{' '}
                    <button
                      onClick={() => onNavigateToLogin?.()}
                      className={`font-medium hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                    >
                      Back to Sign In
                    </button>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
